/**
 * The rules a lens name and alias must satisfy, in one place.
 *
 * Two surfaces create lenses now — the Lenses page and the publish wizard's
 * "new lens" popup — and the alias rule is the kind that rots quietly when
 * copied: the regex and the help text have to agree with each other AND with
 * the API's own `ALIAS_RE`, or the form accepts something the server then
 * rejects with a message the user never saw coming.
 *
 * The dialog SHELLS are still separate on purpose (the Lenses page's is also
 * its edit dialog and carries a processor-template picker the wizard has no
 * use for). What is shared here is the part where drift actually costs
 * something.
 */

/** Mirrors `ALIAS_RE` in `api/src/services/lenses.ts`. */
export const LENS_ALIAS_RE = /^[a-z0-9]([a-z0-9_-]*[a-z0-9])?$/;

export const LENS_ALIAS_HELP =
  'Lowercase letters, digits, underscores, and dashes. Must start and end with a letter or digit. Aliases are globally unique across all organizations.';

/** One placeholder pair everywhere: it shows how the alias follows from the name. */
export const LENS_NAME_PLACEHOLDER = 'City Traffic';
export const LENS_ALIAS_PLACEHOLDER = 'city-traffic';

/** The one toast wording for a lens write, named so a nested create says which lens. */
export const lensCreatedToast = (name: string): string => `Lens "${name}" created`;
export const lensUpdatedToast = (name: string): string => `Lens "${name}" updated`;

export const LENS_NAME_MAX_LENGTH = 100;
export const LENS_ALIAS_MAX_LENGTH = 100;

export type LensFieldErrors = {
  name?: string;
  alias?: string;
};

/**
 * Validates the two required fields, returning per-field messages.
 *
 * Takes raw (untrimmed) input and trims internally, because that is what both
 * callers hold — a form value — and trimming at the call site is the step a
 * second caller forgets.
 */
export function validateLensDraft(draft: { name: string; alias: string }): LensFieldErrors {
  const errors: LensFieldErrors = {};
  const name = draft.name.trim();
  const alias = draft.alias.trim();

  if (!name) errors.name = 'Name is required.';
  else if (name.length > LENS_NAME_MAX_LENGTH) {
    errors.name = `Name must be ${LENS_NAME_MAX_LENGTH} characters or fewer.`;
  }

  if (!alias) errors.alias = 'Alias is required.';
  else if (alias.length > LENS_ALIAS_MAX_LENGTH) {
    errors.alias = `Alias must be ${LENS_ALIAS_MAX_LENGTH} characters or fewer.`;
  } else if (!LENS_ALIAS_RE.test(alias)) errors.alias = LENS_ALIAS_HELP;

  return errors;
}

/**
 * Whether a string is a usable lens alias as it stands — the check a caller
 * makes before putting a typed alias somewhere it will be used verbatim (the
 * Request Builder's snippet), rather than validating a form draft.
 *
 * No trimming and no lowercasing on purpose: an alias with a stray space or a
 * capital is not the alias the API will look up, so it must not pass.
 */
export function isLensAlias(value: string): boolean {
  return value.length > 0 && value.length <= LENS_ALIAS_MAX_LENGTH && LENS_ALIAS_RE.test(value);
}
